<template>
  <base-dialog
    :visible.sync="dialogVisible"
    class="fill-dialog"
    border-radius="none"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    width="1000px"
  >
    <template v-slot:header>
      <div class="header-wrap">
        <div class="title">
          {{ title }}
        </div>
      </div>
    </template>
    <div v-loading="loading" style="min-height: 200px">
      <template v-if="!structLoading">
        <form-group
          ref="formGroup"
          class="form-wrap"
          hide-title
          :groups="[{ controls: fillControls }]"
          :sheetDataServer="sheetDataServer"
          :form-util="formUtil"
          @update="handleFieldUpdate"
          @refDataCreated="handleRefDataCreated"
        ></form-group>
        <sub-table
          v-for="control in fillRelatedControls"
          ref="relatedControl"
          :key="control._cid"
          :parent-data="{ id: dataId, title: sheetData.title, data: form }"
          :only-required="onlyRequired"
          :control="control"
          :available-sheets="availableSheets"
          :fill-setting="getRefTableFillCid(control)"
          :from="from"
          @change="handleRelatedControlChange(control, $event)"
          class="related-control"
        ></sub-table>
        <ref-control
          ref="refControl"
          v-if="fillRefControls.length > 0"
          :only-required="onlyRequired"
          :controls="fillRefControls"
          :availableSheets="availableSheets"
          :form="form"
          :sheetId="sheetId"
          :sheetData="sheetData"
          class="ref-control"
          @full-screen="fullScreen = $event"
          @update="handleRefControlChange"
        ></ref-control>
      </template>
      <!--      如果同时需要填写逾期原因和任务成果，则一起处理。防止任务完成后无法再进行填写-->
      <template v-if="needDueReason">
        <el-form label-position="left" label-width="127px" class="over-due-form">
          <el-form-item>
            <template slot="label">
              <div class="require-label">
                <span class="require-flag">*</span>
                逾期原因
              </div>
            </template>
            <el-select placeholder="请选择" v-model="overDueReason" style="width: 320px">
              <el-option
                v-for="(item, index) in overDueReasonList"
                :value="item"
                :label="item"
                :key="index"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="逾期备注">
            <el-input
              placeholder="请输入内容"
              v-model="overDueRemark"
              style="width: 320px"
            ></el-input>
          </el-form-item>
        </el-form>
      </template>
    </div>
    <template v-slot:footer>
      <template v-if="submitDirectly">
        <base-button type="primary" @click="submit">保存</base-button>
        <base-button @click="close">取消</base-button>
      </template>
      <template v-else>
        <base-button type="primary" @click="submit(true)">仅保存</base-button>
        <base-button type="primary" @click="submit(false)">保存并完成</base-button>
        <base-button @click="close">取消</base-button>
      </template>
    </template>
  </base-dialog>
</template>

<script>
import EditDialog from "@/components/Sheet/EditDialog"
import RefControl from "@/components/Sheet/EditDialog/RefControl"
import { beforeSubmit, transFieldControl } from "@/utils/sheet"
import { loadingService } from "@/utils/ui"
import { fillForm } from "@/api/sheet/sheetBtn"

export default {
  name: "SheetFillDialog",
  extends: EditDialog,
  needFill: Boolean,
  components: {
    RefControl
  },
  props: {
    btnInfo: Object,
    needDueReason: Boolean,
    btnSourceDataId: String, // 按钮填写进入时,传入按钮所在数据详情的数据ID
    btnSourceAppId: String, // 按钮填写进入时,传入按钮所在数据详情的应用ID
    batch: Boolean
  },
  data() {
    return {
      submitDirectly: true, // 用于任务填写提交继承时,控制HTML模板
      overDueReason: "",
      overDueRemark: ""
    }
  },
  computed: {
    title() {
      return this.btnInfo.name + " " + (this.sheetInfo.name || "")
    },
    overDueReasonList() {
      return this.projectInfo?.overDueReasonList || []
    },
    fillCidMap() {
      return this._.chain(this.btnInfo?.fillCids || [])
        .keyBy("cid")
        .mapValues("type")
        .value()
    },
    controls() {
      let cids = (this.btnInfo?.fillCids || []).map(t => t.cid)
      return (this._.cloneDeep(this.sheetInfo.controls) || [])
        .filter(control => cids.includes(control._cid))
        .map(control => {
          control = transFieldControl(control)
          if (this.fillCidMap[control._cid] === "R") {
            control.disabled = true
          } else if (this.fillCidMap[control._cid] === "H") {
            control.required = true
          }
          return control
        })
        .sort((a, b) => {
          // 按照按钮设置的顺序填写
          return cids.findIndex(cid => cid === a._cid) - cids.findIndex(cid => cid === b._cid)
        })
    },
    fillControls() {
      return this.controls.filter(c => c._type !== "ref" || c.oneFlag)
    },
    fillRelatedControls() {
      let cids = this.relatedControls.map(c => c._cid)
      return this.controls.filter(c => cids.includes(c._cid))
    },
    fillRefControls() {
      let cids = this.refControls.map(c => c._cid)
      return this.controls.filter(c => cids.includes(c._cid))
    }
  },
  methods: {
    getRefTableFillCid(control) {
      return (this.btnInfo?.fillCids || []).find(t => t.cid === control._cid)?.subList || []
    },
    async submit() {
      if (this.loading) {
        this.$message.warning("请等待加载完成后操作")
        return
      }
      if (!(await this.validate())) {
        this.scrollToFormValidateErr()
        return
      }
      if (!this.batch) {
        const loading = loadingService({ text: "请求中" })
        try {
          let submitData = beforeSubmit({ form: this.form, fieldsMap: this.formUtil.controlMap })
          let res = await fillForm({
            appId: this.btnSourceAppId,
            btnId: this.btnInfo.id,
            dataId: this.btnSourceDataId,
            data: submitData
          })
          this.$message.success("保存成功！")
          this.$emit("after-submit", res || {}, this.from)
          this.close()
        } finally {
          loading.close()
        }
      } else {
        let submitData = beforeSubmit({ form: this.form, fieldsMap: this.formUtil.controlMap })
        this._.keys(submitData).forEach(key => {
          let value = this.form[key]
          if (!this._.isNumber(value) && !this._.isBoolean(value) && this._.isEmpty(value))
            delete submitData[key]
        })
        this.$emit("select", submitData)
        this.close()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.fill-dialog {
  ::v-deep {
    .el-dialog__body {
      position: relative;
    }
    .require-flag {
      color: red;
      font-size: 24px;
      padding-right: 4px;
      padding-top: 10px;
    }
    .require-label {
      display: flex;
      align-items: center;
    }
    .form-group {
      .form-item {
        width: 100% !important;
      }
    }
  }
  .form-wrap {
    padding: 16px 2px !important;
  }
  .over-due-form{
    display: flex;
    justify-content: space-between;
  }
  .ref-control {
    padding-top: 64px;
    padding-left: 24px;
    padding-right: 4px;
    box-sizing: border-box;
    ::v-deep .sheet-table {
      margin-left: -22px;
    }
  }
  .related-control {
    padding-top: 16px;
    padding-left: 24px;
    padding-right: 4px;
    box-sizing: border-box;
    ::v-deep .el-table--border {
      min-width: 510px;
    }
  }
}
</style>
